import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';
import UserEditSidebar from '../../components/admin/UserEditSidebar';
import ConfirmModal from '../../components/ConfirmModal';
import Layout from '../../components/layout/Layout';

function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [userToDeactivate, setUserToDeactivate] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getAllUsers();
      setUsers(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setSidebarOpen(true);
  };

  const handleDeleteUser = (user) => {
    setUserToDeactivate(user);
    setConfirmOpen(true);
  };

  const confirmDeactivateUser = async () => {
    if (!userToDeactivate) return;
    try {
      await adminApi.deleteUser(userToDeactivate.id);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to deactivate user');
    } finally {
      setConfirmOpen(false);
      setUserToDeactivate(null);
    }
  };

  const getAvatarSrc = (avatar) => {
    if (!avatar) return null;
    if (avatar.startsWith('data:')) return avatar;
    return `data:image/jpeg;base64,${avatar}`;
  };

  const handleCreateUser = () => {
    setSelectedUser(null);
    setSidebarOpen(true);
  };

  const handleUserUpdated = () => {
    fetchUsers();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-eucnet-blue"></div>
          <p className="mt-4 text-slate-600">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <Layout>
      
      {/* Page Title */}
      <div className="bg-white shadow-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-eucnet-navy">User Management</h1>
              <p className="mt-1 text-sm text-slate-600">Manage system users and their permissions</p>
            </div>
            <button
              onClick={handleCreateUser}
              className="px-5 py-2 bg-eucnet-blue text-white rounded-lg hover:bg-eucnet-navy text-sm font-semibold"
            >
              + Create User
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
            {error}
          </div>
        )}

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-eucnet-grey-100">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">
                    Organization
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-700 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          {user.user_info?.avatar ? (
                            <img
                              className="h-10 w-10 rounded-full object-cover"
                              src={getAvatarSrc(user.user_info?.avatar)}
                              alt={`${user.username} avatar`}
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-eucnet-blue flex items-center justify-center text-white font-semibold">
                              {user.username?.charAt(0)?.toUpperCase() || '?'}
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-slate-900">{user.username}</div>
                          {user.user_info && (
                            <div className="text-sm text-slate-500">
                              {user.user_info.first_name} {user.user_info.last_name}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-slate-900">{user.email}</div>
                      {user.user_info?.mobile && (
                        <div className="text-sm text-slate-500">{user.user_info.mobile}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-slate-900">
                        {user.user_info?.organization?.short_name || user.user_info?.organization?.name || 'N/A'}
                      </div>
                      {/*{user.user_info?.city && (
                        <div className="text-sm text-slate-500">{user.user_info.city}</div>
                      )}*/}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        user.role === 'global_admin'
                          ? 'bg-purple-100 text-purple-800'
                          : user.role === 'assessment_admin'
                          ? 'bg-blue-100 text-blue-800'
                          : user.role === 'admin'
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-eucnet-grey-200 text-slate-700'
                      }`}>
                        {user.role === 'global_admin' ? 'Global Admin' : 
                         user.role === 'assessment_admin' ? 'Assessment Admin' :
                         user.role === 'admin' ? 'Admin' : 
                         'User'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        user.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-3">
                        <button
                          onClick={() => handleEditUser(user)}
                          className="text-eucnet-blue hover:text-eucnet-navy transition-colors"
                          title="Edit user"
                        >
                          <svg className="w-5 h-5 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user)}
                          className="text-red-600 hover:text-red-700 transition-colors"
                          title="Deactivate user"
                        >
                          <svg className="w-5 h-5 inline" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
                            <path d="M8 8l8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {users.length === 0 && !loading && (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-slate-900">No users found</h3>
              <p className="mt-1 text-sm text-slate-500">Get started by creating a new user.</p>
            </div>
          )}
        </div>
      </div>

      {/* User Edit Sidebar */}
      <UserEditSidebar
        isOpen={sidebarOpen}
        onClose={() => {
          setSidebarOpen(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        onUserUpdated={handleUserUpdated}
      />
      <ConfirmModal
        open={confirmOpen}
        title="Deactivate user?"
        message={userToDeactivate ? `You are about to deactivate ${userToDeactivate.username}.` : 'You are about to deactivate this user.'}
        confirmText="Deactivate"
        cancelText="Cancel"
        onConfirm={confirmDeactivateUser}
        onCancel={() => {
          setConfirmOpen(false);
          setUserToDeactivate(null);
        }}
      />
    </Layout>
  );
}

export default Users;
